import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, X, ArrowRight, FileText, UserCheck, Clock,
  Stethoscope, MessageSquare, LifeBuoy, Video, LayoutDashboard,
  Ticket, Loader2, ChevronRight, CreditCard,
  UserCircle, HeartPulse, BedDouble, HeartHandshake,
  ClipboardList, MessageSquareWarning, UserMinus, Navigation2,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { useThemeMode } from '../../../../../theme/ThemeProvider';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useRole } from '../../../../../contexts/RoleContext/RoleContext';
import ApiServices from '../../../../../shared/Services/apiService';

type ResultCategory =
  | 'patients'
  | 'doctors'
  | 'coordinators'
  | 'queries'
  | 'documents'
  | 'messages'
  | 'tickets'
  | 'pages';

interface SearchResult {
  id: string;
  label: string;
  subtitle?: string;
  category: ResultCategory;
  path: string;
  icon: React.ElementType;
  iconColor?: string;
}

type ChipId = 'all' | ResultCategory;


const PAGE_LINKS: Record<string, SearchResult[]> = {
  admin: [
    { id: 'p-dash', label: 'Clinical Dashboard', subtitle: 'Portal overview & operational metrics', category: 'pages', path: '/', icon: LayoutDashboard },
    { id: 'p-patients', label: 'Patient Registry', subtitle: 'View and manage all patient records', category: 'pages', path: '/patients', icon: BedDouble },
    { id: 'p-doctors', label: 'Physician Directory', subtitle: 'Specialist profiles & credentials', category: 'pages', path: '/doctors', icon: Stethoscope },
    { id: 'p-nurses', label: 'Care Team', subtitle: 'Care coordinators & nursing staff', category: 'pages', path: '/nurses', icon: HeartHandshake },
    { id: 'p-unassigned', label: 'Pending Assignment', subtitle: 'Patients awaiting physician assignment', category: 'pages', path: '/unassigned-patients', icon: UserMinus },
    { id: 'p-queries', label: 'Message Queue', subtitle: 'Pending clinical message review', category: 'pages', path: '/queries', icon: MessageSquareWarning },
    { id: 'p-video', label: 'Telehealth', subtitle: 'Virtual consultations & video visits', category: 'pages', path: '/video-consultation', icon: Video },
  ],
  nurse: [
    { id: 'p-patients', label: 'Patient Registry', subtitle: 'View and manage all patient records', category: 'pages', path: '/patients', icon: BedDouble },
    { id: 'p-doctors', label: 'Physician Directory', subtitle: 'Specialist profiles & credentials', category: 'pages', path: '/doctors', icon: Stethoscope },
    { id: 'p-unassigned', label: 'Pending Assignment', subtitle: 'Patients awaiting physician assignment', category: 'pages', path: '/unassigned-patients', icon: UserMinus },
    { id: 'p-queries', label: 'Message Queue', subtitle: 'Pending clinical message review', category: 'pages', path: '/queries', icon: MessageSquareWarning },
    { id: 'p-video', label: 'Telehealth', subtitle: 'Virtual consultations & video visits', category: 'pages', path: '/video-consultation', icon: Video },
  ],
  doctor: [
    { id: 'p-patients', label: 'My Patients', subtitle: 'Active patient panel & care plans', category: 'pages', path: '/patients', icon: BedDouble },
    { id: 'p-video', label: 'Telehealth', subtitle: 'Start or schedule virtual consultations', category: 'pages', path: '/video-consultation', icon: Video },
  ],
  patient: [
    { id: 'p-profile', label: 'My Health Profile', subtitle: 'Demographics, insurance & preferences', category: 'pages', path: '/patient-details', icon: UserCircle },
    { id: 'p-docs', label: 'Medical Records', subtitle: 'Lab results, reports & clinical documents', category: 'pages', path: '/patient/reports', icon: ClipboardList },
    { id: 'p-queries', label: 'Provider Messages', subtitle: 'HIPAA-secure messaging with your care team', category: 'pages', path: '/patient/queries', icon: MessageSquare },
    { id: 'p-video', label: 'Telehealth Visit', subtitle: 'Schedule & join virtual appointments', category: 'pages', path: '/video-consultation', icon: Video },
    { id: 'p-tx', label: 'Billing & Payments', subtitle: 'Invoices, claims & payment history', category: 'pages', path: '/transactions', icon: CreditCard },
    { id: 'p-help', label: 'Patient Support', subtitle: 'Help resources, FAQs & HIPAA info', category: 'pages', path: '/help', icon: LifeBuoy },
  ],
};

const CAT_META: Record<ResultCategory, { label: string; icon: React.ElementType; chipLabel: string }> = {
  patients: { label: 'Patients', icon: BedDouble, chipLabel: 'Patients' },
  doctors: { label: 'Physicians', icon: Stethoscope, chipLabel: 'Physicians' },
  coordinators: { label: 'Care Team', icon: HeartHandshake, chipLabel: 'Care Team' },
  queries: { label: 'Clinical Messages', icon: MessageSquareWarning, chipLabel: 'Messages' },
  documents: { label: 'Medical Records', icon: ClipboardList, chipLabel: 'Records' },
  messages: { label: 'Secure Messages', icon: MessageSquare, chipLabel: 'Messages' },
  tickets: { label: 'Support Tickets', icon: Ticket, chipLabel: 'Support' },
  pages: { label: 'Portal Navigation', icon: ChevronRight, chipLabel: 'Navigation' },
};

const ROLE_CHIPS: Record<string, ChipId[]> = {
  admin: ['all', 'patients', 'doctors', 'coordinators', 'queries', 'pages'],
  nurse: ['all', 'patients', 'doctors', 'queries', 'pages'],
  doctor: ['all', 'patients', 'pages'],
  patient: ['all', 'documents', 'messages', 'tickets', 'pages'],
};

function normName(...parts: (string | undefined | null)[]) {
  return parts.filter(Boolean).join(' ').trim();
}

function HeaderSearch() {
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const chipScrollRef = useRef<HTMLDivElement>(null);
  const [dropPos, setDropPos] = useState<{
    inputTop: number;
    rightOffset: number;
    collapsedW: number;
    expandedW: number;
    panelLeft: number;
    panelTop: number;
    panelWidth: number;
  } | null>(null);

  const [isOpen, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeChip, setChip] = useState<ChipId>('all');
  const [isLoading, setLoading] = useState(false);
  const [allResults, setResults] = useState<SearchResult[]>([]);
  const [focusIdx, setFocusIdx] = useState(-1);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('symbian_search_recents') || '[]'); }
    catch { return []; }
  });

  const fetched = useRef(false);

  const { isDark } = useThemeMode();
  const { role } = useRole();
  const navigate = useNavigate();

  const getExpandedW = () => {
    const vw = window.innerWidth;
    if (vw >= 1900) return 560;
    if (vw >= 1400) return 480;
    if (vw >= 1200) return 400;
    if (vw >= 1024) return 340;
    return 300;
  };

  const handleOpen = useCallback(() => {
    if (isOpen) return;
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const expandedW = getExpandedW();
      const panelWidth = Math.min(vw - 32, expandedW + 60);
      const rightOffset = vw - r.right;
      const panelLeft = Math.max(16, r.right - panelWidth);
      setDropPos({
        inputTop: r.top + (r.height - 36) / 2,
        rightOffset,
        collapsedW: r.width,
        expandedW,
        panelLeft,
        panelTop: r.bottom + 8,
        panelWidth,
      });
    }
    setOpen(true);
    setChip('all');
    setFocusIdx(-1);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setQuery('');
    setFocusIdx(-1);
    inputRef.current?.blur();
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      handleClose();
    };
    const id = window.setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => { window.clearTimeout(id); document.removeEventListener('mousedown', handler); };
  }, [isOpen, handleClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) { handleClose(); }
        else { handleOpen(); requestAnimationFrame(() => inputRef.current?.focus()); }
      }
      if (e.key === 'Escape' && isOpen) handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, handleOpen, handleClose]);

  const fetchData = useCallback(async () => {
    if (fetched.current) return;
    fetched.current = true;
    setLoading(true);

    const results: SearchResult[] = [];

    try {
      if (role === 'admin') {
        const [doctorsRes, nursesRes, patientsRes, queryRes] = await Promise.allSettled([
          ApiServices.getDoctors(),
          ApiServices.getNurses(),
          ApiServices.getAllUsers({ userType: 'patient' }),
          ApiServices.getPendingQueries(),
        ]);

        if (doctorsRes.status === 'fulfilled') {
          const doctors = doctorsRes.value?.doctors || doctorsRes.value?.data?.doctors || [];
          doctors.forEach((d: any, idx: number) => {
            results.push({
              id: `doc-${d.PK || d.id || idx}`,
              label: normName(d.FirstName, d.MiddleName, d.LastName) || d.HealthProviderName || d.PatientName || d.email || d.Email || 'Doctor',
              subtitle: d.Speciality ? `Specialist · ${d.Speciality}` : 'Specialist',
              category: 'doctors',
              path: '/doctors',
              icon: Stethoscope,
            });
          });
        }

        if (nursesRes.status === 'fulfilled') {
          const nurses = nursesRes.value?.nurses || nursesRes.value?.data?.nurses || [];
          nurses.forEach((n: any, idx: number) => {
            results.push({
              id: `nur-${n.PK || n.id || idx}`,
              label: normName(n.FirstName, n.MiddleName, n.LastName) || n.HealthProviderName || n.Email || n.email || 'Coordinator',
              subtitle: n.Department ? `Coordinator · ${n.Department}` : 'Care Coordinator',
              category: 'coordinators',
              path: '/nurses',
              icon: HeartPulse,
            });
          });
        }

        if (patientsRes.status === 'fulfilled') {
          const patients = patientsRes.value?.users || patientsRes.value?.data?.users || [];
          patients.forEach((p: any, idx: number) => {
            results.push({
              id: `pat-${p.PK || p.id || idx}`,
              label: normName(p.FirstName, p.MiddleName, p.LastName) || p.PatientName || p.name || 'Patient',
              subtitle: p.MRN ? `MRN: ${p.MRN}` : (p.Email || 'Patient'),
              category: 'patients',
              path: '/patients',
              icon: UserCheck,
            });
          });
        }

        if (queryRes.status === 'fulfilled') {
          const queries = queryRes.value?.queries || queryRes.value?.data?.queries || queryRes.value || [];
          (Array.isArray(queries) ? queries : []).forEach((q: any, idx: number) => {
            results.push({
              id: `qry-${q.id || q.query_id || q.PK || idx}-${idx}`,
              label: q.query || q.Query || q.message || 'Query',
              subtitle: `Status: ${q.status || q.Status || 'Pending'}`,
              category: 'queries',
              path: '/queries',
              icon: MessageSquare,
            });
          });
        }

      } else if (role === 'nurse') {
        const [doctorsRes, patientsRes, queryRes] = await Promise.allSettled([
          ApiServices.getDoctors(),
          ApiServices.getAllUsers({ userType: 'patient' }),
          ApiServices.getPendingQueries(),
        ]);

        if (doctorsRes.status === 'fulfilled') {
          const doctors = doctorsRes.value?.doctors || doctorsRes.value?.data?.doctors || [];
          doctors.forEach((d: any, idx: number) => {
            results.push({
              id: `doc-${d.PK || d.id || idx}`,
              label: normName(d.FirstName, d.MiddleName, d.LastName) || d.HealthProviderName || d.PatientName || d.email || d.Email || 'Doctor',
              subtitle: d.Speciality ? `Specialist · ${d.Speciality}` : 'Specialist',
              category: 'doctors',
              path: '/doctors',
              icon: Stethoscope,
            });
          });
        }

        if (patientsRes.status === 'fulfilled') {
          const patients = patientsRes.value?.users || patientsRes.value?.data?.users || [];
          patients.forEach((p: any, idx: number) => {
            results.push({
              id: `pat-${p.PK || p.id || idx}`,
              label: normName(p.FirstName, p.MiddleName, p.LastName) || p.PatientName || 'Patient',
              subtitle: p.MRN ? `MRN: ${p.MRN}` : (p.Email || 'Patient'),
              category: 'patients',
              path: '/patients',
              icon: UserCheck,
            });
          });
        }

        if (queryRes.status === 'fulfilled') {
          const queries = queryRes.value?.queries || queryRes.value?.data?.queries || queryRes.value || [];
          (Array.isArray(queries) ? queries : []).forEach((q: any, idx: number) => {
            results.push({
              id: `qry-${q.id || q.query_id || q.PK || idx}-${idx}`,
              label: q.query || q.Query || q.message || 'Query',
              subtitle: `Status: ${q.status || q.Status || 'Pending'}`,
              category: 'queries',
              path: '/queries',
              icon: MessageSquare,
            });
          });
        }

      } else if (role === 'doctor') {
        const res = await ApiServices.getAssignedPatients().catch(() => null);
        const patients = res?.patients || res?.data?.patients || res?.data || [];
        (Array.isArray(patients) ? patients : []).forEach((p: any) => {
          results.push({
            id: `pat-${p.PK || p.id}`,
            label: normName(p.FirstName, p.MiddleName, p.LastName) || p.PatientName || p.name || 'Patient',
            subtitle: p.MRN ? `MRN: ${p.MRN}` : (p.Email || 'Assigned Patient'),
            category: 'patients',
            path: '/patients',
            icon: UserCheck,
          });
        });

      } else if (role === 'patient') {
        const [docsRes, ticketsRes] = await Promise.allSettled([
          ApiServices.getMedicalDocs(),
          ApiServices.getSupportTickets(),
        ]);

        if (docsRes.status === 'fulfilled') {
          const docs = docsRes.value?.docs || docsRes.value?.data?.docs || docsRes.value || [];
          (Array.isArray(docs) ? docs : []).forEach((d: any) => {
            results.push({
              id: `doc-${d.id || d.PK || d.document_id}`,
              label: d.document_name || d.DocumentName || d.name || d.FileName || 'Document',
              subtitle: d.ItemType || d.document_type || 'Medical Document',
              category: 'documents',
              path: '/patient/reports',
              icon: FileText,
            });
          });
        }

        if (ticketsRes.status === 'fulfilled') {
          const tickets = ticketsRes.value?.tickets || ticketsRes.value?.data || ticketsRes.value || [];
          (Array.isArray(tickets) ? tickets : []).forEach((t: any) => {
            results.push({
              id: `tkt-${t.id || t.ticket_id || t.PK}`,
              label: t.message || t.category || t.title || 'Support Ticket',
              subtitle: `Category: ${t.category || 'General'} · ${t.status || 'Open'}`,
              category: 'tickets',
              path: '/help/view-ticket',
              icon: Ticket,
            });
          });
        }
      }
    } catch {
      /* Fail silently; page links always work */
    }

    setResults(results);
    setLoading(false);
  }, [role]);

  useEffect(() => {
    if (isOpen) {
      fetched.current = false;
      fetchData();
    }
  }, [isOpen, fetchData]);

  const filteredResults = useMemo<SearchResult[]>(() => {
    const pages = PAGE_LINKS[role] ?? [];
    const all = [...allResults, ...pages];
    const q = query.trim().toLowerCase();

    const filtered = q
      ? all.filter(r =>
        r.label.toLowerCase().includes(q) ||
        (r.subtitle?.toLowerCase().includes(q))
      )
      : all;

    if (activeChip === 'all') return filtered;
    return filtered.filter(r => r.category === activeChip);
  }, [allResults, query, activeChip, role]);

  const grouped = useMemo(() => {
    const map = new Map<ResultCategory, SearchResult[]>();
    for (const r of filteredResults) {
      if (!map.has(r.category)) map.set(r.category, []);
      map.get(r.category)!.push(r);
    }
    // Preferred order
    const order: ResultCategory[] = ['patients', 'doctors', 'coordinators', 'queries', 'documents', 'messages', 'tickets', 'pages'];
    const ordered: [ResultCategory, SearchResult[]][] = [];
    for (const cat of order) {
      if (map.has(cat)) ordered.push([cat, map.get(cat)!]);
    }
    return ordered;
  }, [filteredResults]);

  const flatList = useMemo(() => filteredResults, [filteredResults]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIdx(i => Math.min(i + 1, flatList.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIdx(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && focusIdx >= 0) {
      e.preventDefault();
      const r = flatList[focusIdx];
      if (r) { navigate(r.path); handleClose(); }
    }
  }, [flatList, focusIdx, navigate, handleClose]);

  useEffect(() => {
    if (focusIdx >= 0 && listRef.current) {
      const el = listRef.current.querySelector(`[data-idx="${focusIdx}"]`) as HTMLElement;
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusIdx]);

  const handleSelect = (r: SearchResult) => {
    if (query.trim()) {
      const updated = [query.trim(), ...recentSearches.filter(q => q !== query.trim())].slice(0, 5);
      setRecentSearches(updated);
      try { localStorage.setItem('symbian_search_recents', JSON.stringify(updated)); } catch { }
    }
    navigate(r.path);
    handleClose();
  };

  const chips = ROLE_CHIPS[role] ?? ['all', 'pages'];

  const chipIconMap: Record<string, React.ElementType> = {
    all: Search, patients: BedDouble, doctors: Stethoscope,
    coordinators: HeartHandshake, queries: MessageSquareWarning,
    documents: ClipboardList, messages: MessageSquare,
    tickets: Ticket, pages: Navigation2,
  };

  const catResultColor: Record<string, string> = {
    patients: isDark ? '#34d399' : '#047857', doctors: isDark ? '#38bdf8' : '#0369a1',
    coordinators: isDark ? '#a78bfa' : '#6d28d9', queries: isDark ? '#fbbf24' : '#92400e',
    documents: isDark ? '#2dd4bf' : '#0f766e', messages: isDark ? '#818cf8' : '#3730a3',
    tickets: isDark ? '#fb7185' : '#be123c', pages: isDark ? '#60a5fa' : '#1d4ed8',
  };

  const modal = (
    <AnimatePresence>
      {isOpen && dropPos && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={handleClose}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9998,
              background: isDark ? 'rgba(2,10,27,0.55)' : 'rgba(15,23,42,0.18)',
              backdropFilter: 'blur(3px)',
              WebkitBackdropFilter: 'blur(3px)',
            }}
          />

          <motion.div
            initial={{ width: dropPos.collapsedW, opacity: 0.6 }}
            animate={{ width: dropPos.expandedW, opacity: 1 }}
            exit={{ width: dropPos.collapsedW, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            style={{
              position: 'fixed',
              top: dropPos.inputTop,
              right: dropPos.rightOffset,
              height: 36,
              zIndex: 10001,
              overflow: 'hidden',
              borderRadius: 12,
            }}
            className={[
              'flex items-center gap-2 px-3',
              'ring-1 ring-inset',
              isDark
                ? 'bg-[#0D1528] ring-brand-500/50 shadow-[0_0_0_1px_rgba(3,93,184,0.3),0_4px_20px_rgba(0,0,0,0.5)]'
                : 'bg-white ring-brand-400/70 shadow-[0_0_0_1px_rgba(3,93,184,0.15),0_4px_16px_rgba(3,93,184,0.12)]',
            ].join(' ')}
          >
            <Search size={13} strokeWidth={2.3} className="flex-shrink-0 text-brand-500" />
            <input
              ref={inputRef}
              autoFocus
              value={query}
              onChange={e => { setQuery(e.target.value); setFocusIdx(-1); }}
              onKeyDown={handleKeyDown}
              placeholder={
                role === 'admin' ? 'Search patients, physicians, messages…' :
                  role === 'nurse' ? 'Search patients, care team, messages…' :
                    role === 'doctor' ? 'Search patient panel by name or MRN…' :
                      'Search records, messages, billing…'
              }
              className={[
                'flex-1 min-w-0 bg-transparent outline-none text-[12.5px] font-medium',
                isDark ? 'text-slate-100 placeholder:text-slate-500' : 'text-slate-800 placeholder:text-slate-400',
              ].join(' ')}
              aria-label="Clinical search"
            />
            {query
              ? <button
                onClick={e => { e.stopPropagation(); setQuery(''); setFocusIdx(-1); inputRef.current?.focus(); }}
                className={['w-5 h-5 flex-shrink-0 rounded flex items-center justify-center transition-colors',
                  isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'].join(' ')}>
                <X size={11} />
              </button>
              : <span className={['text-[10px] font-semibold whitespace-nowrap hidden sm:block flex-shrink-0',
                isDark ? 'text-slate-600' : 'text-slate-400'].join(' ')}>Esc to close</span>
            }
          </motion.div>

          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="fixed flex flex-col overflow-hidden"
            style={{
              top: dropPos.panelTop,
              left: dropPos.panelLeft,
              width: dropPos.panelWidth,
              zIndex: 10002,
              minWidth: 320,
              maxWidth: 'min(90vw, 680px)',
              maxHeight: 'min(72vh, 540px)',
              minHeight: 280,
              background: isDark ? '#0D1528' : '#FFFFFF',
              borderRadius: 14,
              boxShadow: isDark
                ? '0 0 0 1px rgba(255,255,255,0.09), 0 16px 48px -6px rgba(0,0,0,0.85), 0 4px 16px rgba(3,93,184,0.15)'
                : '0 0 0 1px rgba(203,213,225,0.75), 0 12px 40px -6px rgba(3,93,184,0.14), 0 4px 12px rgba(0,0,0,0.07)',
            }}
          >
            {/* Top accent bar */}
            <div className="h-[3px] flex-shrink-0" style={{ background: 'linear-gradient(90deg,#035DB8,#2B8DE0,#5EAFF0)' }} />

            {/* ── Category filter chips — only when query is active ── */}
            {query && (
              <div
                className={[
                  'relative flex-shrink-0',
                  isDark ? 'border-b border-white/[0.06]' : 'border-b border-slate-100',
                ].join(' ')}
              >
                {/* Right-fade gradient to indicate overflow */}
                <div
                  className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 z-10"
                  style={{
                    background: isDark
                      ? 'linear-gradient(to left, #0D1528 60%, transparent)'
                      : 'linear-gradient(to left, #ffffff 60%, transparent)',
                  }}
                />
                <div
                  ref={chipScrollRef}
                  onWheel={e => {
                    e.preventDefault();
                    e.currentTarget.scrollLeft += e.deltaY + e.deltaX;
                  }}
                  onMouseDown={e => {
                    const el = chipScrollRef.current;
                    if (!el) return;
                    const startX = e.pageX;
                    const startScroll = el.scrollLeft;
                    let moved = false;

                    const onMove = (ev: MouseEvent) => {
                      const dx = ev.pageX - startX;
                      if (Math.abs(dx) > 4) {
                        moved = true;
                        el.style.cursor = 'grabbing';
                        el.scrollLeft = startScroll - dx;
                      }
                    };
                    const onUp = () => {
                      el.style.cursor = '';
                      document.removeEventListener('mousemove', onMove);
                      document.removeEventListener('mouseup', onUp);
                      // Suppress the upcoming click if we actually dragged
                      if (moved) {
                        const suppress = (ev: MouseEvent) => { ev.stopPropagation(); ev.preventDefault(); };
                        document.addEventListener('click', suppress, { capture: true, once: true });
                      }
                    };
                    document.addEventListener('mousemove', onMove);
                    document.addEventListener('mouseup', onUp);
                  }}
                  className={[
                    'flex items-center gap-1.5 px-4 py-2 overflow-x-auto select-none cursor-grab active:cursor-grabbing',
                    isDark ? 'bg-white/[0.015]' : 'bg-slate-50/50',
                  ].join(' ')}
                  style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    WebkitOverflowScrolling: 'touch',
                  } as React.CSSProperties}
                >
                  {chips.map(chip => {
                    const ChipIcon = chipIconMap[chip];
                    const chipCount = chip === 'all'
                      ? filteredResults.length
                      : filteredResults.filter(r => r.category === chip).length;
                    const isActive = activeChip === chip;
                    return (
                      <button
                        key={chip}
                        onClick={() => { setChip(chip); setFocusIdx(-1); }}
                        className={[
                          'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold transition-all duration-150 whitespace-nowrap flex-shrink-0',
                          isActive
                            ? isDark ? 'bg-brand-500/20 text-brand-200 ring-1 ring-brand-500/35' : 'bg-brand-600 text-white shadow-sm'
                            : isDark ? 'text-slate-400 hover:text-slate-200 hover:bg-white/[0.06]' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100',
                        ].join(' ')}
                      >
                        {ChipIcon && <ChipIcon size={10} />}
                        {chip === 'all' ? 'All' : CAT_META[chip]?.chipLabel ?? chip}
                        {chipCount > 0 && (
                          <span className={[
                            'ml-0.5 px-1.5 py-px rounded-full text-[9px] font-bold',
                            isActive
                              ? isDark ? 'bg-brand-400/30 text-brand-200' : 'bg-white/25 text-white'
                              : isDark ? 'bg-white/[0.08] text-slate-400' : 'bg-slate-200 text-slate-500',
                          ].join(' ')}>
                            {chipCount}
                          </span>
                        )}
                      </button>
                    );
                  })}
                  {/* Spacer to ensure last chip clears the fade gradient */}
                  <div className="flex-shrink-0 w-8" />
                </div>
              </div>
            )}

            {/* ── Body ── */}
            <div
              ref={listRef}
              className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-[4px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-brand-400/30"
              style={{ background: isDark ? '#0A1020' : '#F8FAFC' }}
            >
              {isLoading ? (
                /* ── Loading skeletons ── */
                <div className="p-4 space-y-2">
                  {[80, 65, 72, 55, 70].map((w, i) => (
                    <div key={i} className="flex items-center gap-3 px-2 py-2.5 animate-pulse">
                      <div className={`w-9 h-9 rounded-xl flex-shrink-0 ${isDark ? 'bg-white/[0.05]' : 'bg-slate-200/60'}`} />
                      <div className="flex-1 space-y-2">
                        <div className={`h-3 rounded-full ${isDark ? 'bg-white/[0.06]' : 'bg-slate-200/70'}`} style={{ width: `${w}%` }} />
                        <div className={`h-2.5 rounded-full ${isDark ? 'bg-white/[0.04]' : 'bg-slate-200/50'}`} style={{ width: `${w * 0.6}%` }} />
                      </div>
                    </div>
                  ))}
                </div>

              ) : !query ? (
                /* ── Home state: premium US healthcare SaaS layout ── */
                <div className="flex flex-col divide-y divide-transparent">

                  {/* ── Recent Searches (only when recents exist) ── */}
                  {recentSearches.length > 0 && (
                    <div className={[
                      'px-4 pt-3.5 pb-2.5',
                      isDark ? 'border-b border-white/[0.05]' : 'border-b border-slate-100',
                    ].join(' ')}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={[
                          'text-[9.5px] font-bold uppercase tracking-[0.14em]',
                          isDark ? 'text-slate-500' : 'text-slate-400',
                        ].join(' ')}>Recent</span>
                        <button
                          onClick={() => {
                            setRecentSearches([]);
                            try { localStorage.removeItem('symbian_search_recents'); } catch { }
                          }}
                          className={[
                            'text-[10px] font-medium transition-colors',
                            isDark ? 'text-slate-600 hover:text-slate-400' : 'text-slate-400 hover:text-slate-600',
                          ].join(' ')}
                        >Clear all</button>
                      </div>
                      <div className="space-y-px">
                        {recentSearches.map(q => (
                          <div key={q} className={[
                            'flex items-center gap-2.5 px-2 py-1.5 rounded-lg group transition-colors',
                            isDark ? 'hover:bg-white/[0.05]' : 'hover:bg-slate-50',
                          ].join(' ')}>
                            <Clock size={12} className={isDark ? 'text-slate-600 flex-shrink-0' : 'text-slate-400 flex-shrink-0'} />
                            <button
                              className={[
                                'flex-1 text-left text-[12px] font-medium truncate',
                                isDark ? 'text-slate-400' : 'text-slate-600',
                              ].join(' ')}
                              onClick={() => {
                                setQuery(q);
                                setFocusIdx(-1);
                                requestAnimationFrame(() => inputRef.current?.focus());
                              }}
                            >{q}</button>
                            <button
                              onClick={() => {
                                const updated = recentSearches.filter(r => r !== q);
                                setRecentSearches(updated);
                                try { localStorage.setItem('symbian_search_recents', JSON.stringify(updated)); } catch { }
                              }}
                              className={[
                                'w-4 h-4 flex items-center justify-center rounded transition-all',
                                'opacity-0 group-hover:opacity-100',
                                isDark ? 'text-slate-600 hover:text-slate-300 hover:bg-white/[0.08]' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200',
                              ].join(' ')}
                            ><X size={10} /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Navigate To ── compact pill links ── */}
                  <div className={[
                    'px-4 pt-3.5 pb-3',
                    isDark ? 'border-b border-white/[0.05]' : 'border-b border-slate-100',
                  ].join(' ')}>
                    <p className={[
                      'text-[9.5px] font-bold uppercase tracking-[0.14em] mb-2',
                      isDark ? 'text-slate-500' : 'text-slate-400',
                    ].join(' ')}>Navigate To</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(PAGE_LINKS[role] ?? []).map(page => {
                        const Icon = page.icon;
                        return (
                          <button
                            key={page.id}
                            onClick={() => handleSelect(page)}
                            className={[
                              'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg ring-1 ring-inset',
                              'text-[11px] font-medium transition-all duration-150 group',
                              isDark
                                ? 'bg-white/[0.04] ring-white/[0.07] text-slate-400 hover:bg-brand-500/[0.14] hover:ring-brand-500/[0.28] hover:text-brand-200'
                                : 'bg-white ring-slate-200/90 text-slate-600 hover:bg-brand-50 hover:ring-brand-200/80 hover:text-brand-700 shadow-[0_1px_2px_rgba(0,0,0,0.05)]',
                            ].join(' ')}
                          >
                            <Icon size={11} className={[
                              'flex-shrink-0 transition-colors',
                              isDark ? 'text-slate-600 group-hover:text-brand-400' : 'text-slate-400 group-hover:text-brand-500',
                            ].join(' ')} />
                            {page.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── Search Across ── actionable example rows ── */}
                  <div className="px-4 pt-3 pb-3">
                    <p className={[
                      'text-[9.5px] font-bold uppercase tracking-[0.14em] mb-1',
                      isDark ? 'text-slate-500' : 'text-slate-400',
                    ].join(' ')}>Search Across</p>

                    {(role === 'admin' || role === 'nurse'
                      ? [
                        { icon: BedDouble, label: 'Patient name or MRN', example: 'John Smith' },
                        { icon: Stethoscope, label: 'Physician name or specialty', example: 'Cardiology' },
                        { icon: MessageSquareWarning, label: 'Pending clinical messages', example: 'Query #2041' },
                      ]
                      : role === 'doctor'
                        ? [
                          { icon: BedDouble, label: 'Patient name or MRN', example: 'Jane Doe' },
                        ]
                        : [
                          { icon: ClipboardList, label: 'Medical records & reports', example: 'Lab results' },
                          { icon: MessageSquare, label: 'Provider messages', example: 'Message' },
                          { icon: CreditCard, label: 'Billing & claim status', example: 'Invoice' },
                          { icon: LifeBuoy, label: 'Help articles & FAQs', example: 'HIPAA rights' },
                        ]
                    ).map(({ icon: HintIcon, label, example }) => (
                      <button
                        key={label}
                        onClick={() => {
                          setQuery(example);
                          setFocusIdx(-1);
                          requestAnimationFrame(() => inputRef.current?.focus());
                        }}
                        className={[
                          'w-full flex items-center gap-3 px-2 py-[7px] rounded-lg text-left transition-colors duration-100 group',
                          isDark ? 'hover:bg-white/[0.05]' : 'hover:bg-slate-50',
                        ].join(' ')}
                      >
                        <div className={[
                          'w-[26px] h-[26px] rounded-md flex items-center justify-center flex-shrink-0 ring-1 ring-inset transition-all',
                          isDark
                            ? 'bg-white/[0.04] ring-white/[0.07] group-hover:bg-brand-500/15 group-hover:ring-brand-500/25'
                            : 'bg-white ring-slate-200/80 group-hover:bg-brand-50 group-hover:ring-brand-200/70 shadow-[0_1px_2px_rgba(0,0,0,0.05)]',
                        ].join(' ')}>
                          <HintIcon size={12} className={[
                            'transition-colors duration-150',
                            isDark ? 'text-slate-500 group-hover:text-brand-400' : 'text-slate-400 group-hover:text-brand-500',
                          ].join(' ')} />
                        </div>
                        <span className={[
                          'flex-1 text-[12px] font-medium',
                          isDark ? 'text-slate-400 group-hover:text-slate-200' : 'text-slate-600 group-hover:text-slate-800',
                        ].join(' ')}>{label}</span>
                        <span className={[
                          'flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-md transition-all duration-150',
                          'translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100',
                          isDark ? 'text-brand-400 bg-brand-500/[0.12] ring-1 ring-brand-500/20' : 'text-brand-600 bg-brand-50 ring-1 ring-brand-200/60',
                        ].join(' ')}>
                          Try <ArrowRight size={9} />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

              ) : flatList.length === 0 ? (
                /* ── No results empty state ── */
                <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                  <div className={[
                    'w-14 h-14 rounded-2xl flex items-center justify-center mb-4',
                    isDark ? 'bg-white/[0.05]' : 'bg-slate-100',
                  ].join(' ')}>
                    <Search size={22} className={isDark ? 'text-slate-600' : 'text-slate-300'} />
                  </div>
                  <p className={['text-[14px] font-bold mb-1', isDark ? 'text-white' : 'text-slate-700'].join(' ')}>
                    No results for "{query}"
                  </p>
                  <p className={['text-[12px] mb-4', isDark ? 'text-slate-500' : 'text-slate-400'].join(' ')}>
                    Try a different spelling or shorter keyword
                  </p>
                  <button
                    onClick={() => { setQuery(''); setChip('all'); inputRef.current?.focus(); }}
                    className={[
                      'inline-flex items-center gap-2 px-4 py-2 rounded-xl text-[12px] font-semibold ring-1 ring-inset transition-colors',
                      isDark ? 'text-brand-300 bg-brand-500/10 ring-brand-500/20 hover:bg-brand-500/20' : 'text-brand-600 bg-brand-50 ring-brand-200 hover:bg-brand-100',
                    ].join(' ')}
                  >
                    <X size={12} /> Clear search
                  </button>
                </div>

              ) : (
                /* ── Grouped results ── */
                (() => {
                  let globalIdx = 0;
                  return (
                    <div className="py-2">
                      {grouped.map(([cat, items]) => {
                        const meta = CAT_META[cat];
                        const CatIcon = meta.icon;
                        const iconColor = catResultColor[cat] ?? '#64748b';
                        const startIdx = globalIdx;
                        globalIdx += items.length;

                        return (
                          <div key={cat} className="mb-1">
                            {/* Category header */}
                            <div className={[
                              'flex items-center gap-2 px-4 sm:px-5 pt-3 pb-2',
                            ].join(' ')}>
                              <CatIcon size={11} className={isDark ? 'text-slate-500' : 'text-slate-400'} />
                              <span className={[
                                'text-[10px] font-bold uppercase tracking-[0.12em]',
                                isDark ? 'text-slate-500' : 'text-slate-400',
                              ].join(' ')}>{meta.label}</span>
                              <span className={[
                                'text-[10px] font-bold px-1.5 py-0.5 rounded-full',
                                isDark ? 'bg-white/[0.06] text-slate-500' : 'bg-slate-200 text-slate-500',
                              ].join(' ')}>{items.length}</span>
                            </div>

                            {/* Result rows */}
                            {items.map(r => {
                              const idx = startIdx + items.indexOf(r);
                              const isFocused = focusIdx === idx;
                              const Icon = r.icon;

                              return (
                                <button
                                  key={r.id}
                                  data-idx={idx}
                                  onClick={() => handleSelect(r)}
                                  onMouseEnter={() => setFocusIdx(idx)}
                                  className={[
                                    'w-full flex items-center gap-3 px-4 sm:px-5 py-2.5 text-left transition-colors duration-75 group',
                                    isFocused
                                      ? isDark ? 'bg-brand-500/[0.12]' : 'bg-brand-50/80'
                                      : isDark ? 'hover:bg-white/[0.035]' : 'hover:bg-white/80',
                                  ].join(' ')}
                                >
                                  {/* Icon */}
                                  <div
                                    className={[
                                      'w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ring-1 ring-inset transition-colors',
                                      isFocused
                                        ? isDark ? 'bg-brand-500/20 ring-brand-500/30' : 'bg-brand-100 ring-brand-200'
                                        : isDark ? 'bg-white/[0.05] ring-white/[0.07]' : 'bg-white ring-slate-200/70',
                                    ].join(' ')}
                                  >
                                    <Icon size={15} style={{ color: isFocused ? (isDark ? '#5EAFF0' : '#035DB8') : iconColor }} />
                                  </div>

                                  {/* Label + subtitle */}
                                  <div className="flex-1 min-w-0">
                                    <p className={[
                                      'text-[13px] font-semibold truncate leading-tight',
                                      isFocused
                                        ? isDark ? 'text-white' : 'text-brand-700'
                                        : isDark ? 'text-slate-200' : 'text-slate-700',
                                    ].join(' ')}>{r.label}</p>
                                    {r.subtitle && (
                                      <p className={[
                                        'text-[11px] truncate mt-0.5',
                                        isDark ? 'text-slate-500' : 'text-slate-400',
                                      ].join(' ')}>{r.subtitle}</p>
                                    )}
                                  </div>

                                  {/* Arrow indicator */}
                                  <div className={[
                                    'flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-150',
                                    isFocused
                                      ? isDark ? 'bg-brand-500/20 text-brand-300' : 'bg-brand-100 text-brand-600'
                                      : 'opacity-0 group-hover:opacity-100',
                                  ].join(' ')}>
                                    <ArrowRight size={13} />
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              )}
            </div>

            {/* ── Footer: keyboard shortcuts + result count ── */}
            <div
              className={[
                'flex items-center justify-between px-4 sm:px-5 py-2.5 flex-shrink-0',
                isDark ? 'border-t border-white/[0.06] bg-white/[0.015]' : 'border-t border-slate-100 bg-slate-50/80',
              ].join(' ')}
            >
              <div className="flex items-center gap-4">
                {[
                  { key: '↩', label: 'Open' },
                  { key: '↑↓', label: 'Navigate' },
                  { key: 'Esc', label: 'Close' },
                ].map(({ key, label }) => (
                  <div key={key} className="hidden sm:flex items-center gap-1.5">
                    <kbd className={[
                      'inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-bold',
                      isDark ? 'bg-white/[0.07] text-slate-400 border border-white/[0.1]' : 'bg-white text-slate-500 border border-slate-200 shadow-sm',
                    ].join(' ')}>{key}</kbd>
                    <span className={['text-[10.5px]', isDark ? 'text-slate-600' : 'text-slate-400'].join(' ')}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <div
        ref={triggerRef}
        onClick={handleOpen}
        aria-label="Open search"
        style={{ transition: 'opacity 0.15s, width 0.22s cubic-bezier(0.22,1,0.36,1)' }}
        className={[
          'hidden md:flex items-center gap-2 h-9 px-3 rounded-xl ring-1 ring-inset cursor-pointer select-none',
          'w-9 xl:w-auto',
          isOpen
            ? 'opacity-0 pointer-events-none'
            : isDark
              ? 'bg-white/[0.06] ring-white/[0.10] hover:bg-white/[0.09] hover:ring-white/[0.16]'
              : 'bg-slate-50 ring-slate-200/80 hover:bg-white hover:ring-brand-200/70',
        ].join(' ')}
      >
        {isLoading
          ? <Loader2 size={14} className="flex-shrink-0 animate-spin text-brand-400" />
          : <Search size={14} strokeWidth={2.2} className={[
            'flex-shrink-0 transition-colors',
            isDark ? 'text-slate-400' : 'text-slate-500',
          ].join(' ')} />
        }
        <span className={[
          'hidden xl:block text-[12px] font-medium whitespace-nowrap',
          isDark ? 'text-slate-400' : 'text-slate-500',
        ].join(' ')}>Search</span>
        <kbd className={[
          'hidden 2xl:inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold pointer-events-none',
          isDark ? 'bg-white/[0.07] text-slate-500 border border-white/[0.08]' : 'bg-white text-slate-400 border border-slate-200 shadow-sm',
        ].join(' ')}>⌘K</kbd>
      </div>

      <motion.button
        whileTap={{ scale: 0.88 }}
        onClick={handleOpen}
        className={[
          'md:hidden w-9 h-9 rounded-xl flex items-center justify-center ring-1 ring-inset transition-all duration-200',
          isDark ? 'bg-white/[0.06] ring-white/[0.10] text-slate-400' : 'bg-slate-50 ring-slate-200 text-slate-500',
        ].join(' ')}
        aria-label="Open search"
      >
        <Search size={16} strokeWidth={2.1} />
      </motion.button>

      {createPortal(modal, document.body)}
    </>
  );
}

export default HeaderSearch;